import React, { useState, useEffect } from 'react';
import {PackageInfo} from "../UserCanister/candid/main.did";

interface MotokoPackagesDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onAddPackage: (packageInfo: PackageInfo) => Promise<string | null>;
    onRemovePackage: (packageName: string) => Promise<string | null>;
    currentPackages: PackageInfo[];
}

const VESSEL_PACKAGES = [
    { name: 'base', description: 'The Motoko standard library', repo: 'dfinity/motoko-base', version: 'master', dir: 'src' },
    { name: 'crud', description: 'A framework for constructing CRUD services', repo: 'matthewhammer/motoko-crud', version: 'master', dir: 'src' },
    { name: 'matchers', description: 'Composable Assertions for unit testing', repo: 'kritzcreek/motoko-matchers', version: 'master', dir: 'src' },
    { name: 'parsec', description: 'A Parsec-based parsing combinator library', repo: 'crusso/mo-parsec', version: 'main', dir: 'src' },
    { name: 'scc', description: 'A Strongly Connected Component library', repo: 'nomeata/motoko-scc', version: 'v0.1.1', dir: 'src' },
    { name: 'sha256', description: 'SHA256 implemented in Motoko', repo: 'enzoh/motoko-sha', version: 'master', dir: 'src' },
    { name: 'icip', description: 'The Internet Computer Improvement Protocol library', repo: 'dfinity/icip', version: 'master', dir: 'src' },
    { name: 'pretty', description: 'A pretty printing library', repo: 'dfinity/pretty', version: 'master', dir: 'src' },
    { name: 'sha224', description: 'SHA224 implemented in Motoko', repo: 'enzoh/motoko-sha', version: 'master', dir: 'src' },
    { name: 'sha2', description: 'Optimized implementation of all SHA2 functions', repo: 'research-ag/sha2', version: '0.1.0', dir: 'src' },
    { name: 'cn-logger', description: 'A comprehensive logging system for Internet Computer canisters', repo: 'coinnation/cnLogger', version: '0.1.1', dir: 'src' },
];

const MotokoPackagesDialog: React.FC<MotokoPackagesDialogProps> = ({
                                                                       isOpen,
                                                                       onClose,
                                                                       onAddPackage,
                                                                       onRemovePackage,
                                                                       currentPackages
                                                                   }) => {
    const [activeTab, setActiveTab] = useState<'vessel' | 'mops'>('vessel');

    // Mops tab state
    const [mopsPackageName, setMopsPackageName] = useState('');
    const [mopsRepoUrl, setMopsRepoUrl] = useState('');
    const [mopsVersion, setMopsVersion] = useState('');
    const [mopsSubdir, setMopsSubdir] = useState('');
    const [mopsFormat, setMopsFormat] = useState<'simple' | 'github'>('github');

    const [error, setError] = useState<string | null>(null);
    const [installedPackages, setInstalledPackages] = useState<Set<string>>(new Set());

    useEffect(() => {
        const packageNames = new Set(currentPackages.map(pkg => pkg.name.trim()));
        setInstalledPackages(packageNames);
    }, [currentPackages]);

    const isPackageInstalled = (name: string): boolean => {
        return installedPackages.has(name.trim());
    };

    const handlePackageAction = async (pkg: typeof VESSEL_PACKAGES[0]) => {
        const isInstalled = isPackageInstalled(pkg.name);
        try {
            if (isInstalled) {
                await onRemovePackage(pkg.name);
                setInstalledPackages(prev => {
                    const next = new Set(prev);
                    next.delete(pkg.name.trim());
                    return next;
                });
            } else {
                const packageInfo: PackageInfo = {
                    name: pkg.name,
                    repo: pkg.repo,
                    version: pkg.version,
                    dir: pkg.dir ? [[pkg.dir]] : [],
                    homepage: pkg.repo ? [[`https://github.com/${pkg.repo}`]] : []
                };
                await onAddPackage(packageInfo);
                setInstalledPackages(prev => new Set([...prev, pkg.name.trim()]));
            }
            setError(null);
        } catch (err) {
            setError(isInstalled ? 'Failed to remove package' : 'Failed to add package');
            console.error('Package action error:', err);
        }
    };

    const handleAddMopsPackage = async () => {
        try {
            if (!mopsPackageName) {
                throw new Error('Package name is required');
            }

            let packageInfo: PackageInfo;

            if (mopsFormat === 'simple') {
                // Simple format (just package name and version)
                if (!mopsVersion) {
                    throw new Error('Version is required');
                }

                packageInfo = {
                    name: mopsPackageName,
                    repo: '',
                    version: mopsVersion,
                    dir: [],
                    homepage: []
                };
            } else {
                // GitHub format
                if (!mopsRepoUrl) {
                    throw new Error('GitHub repository is required');
                }

                if (!mopsVersion) {
                    throw new Error('Version/branch is required');
                }

                // Extract owner/repo from GitHub URL
                let repo = mopsRepoUrl;
                if (mopsRepoUrl.startsWith('https://github.com/')) {
                    repo = mopsRepoUrl.replace('https://github.com/', '');
                }

                // Remove .git suffix if present
                repo = repo.replace(/\.git$/, '');

                packageInfo = {
                    name: mopsPackageName,
                    repo: repo,
                    version: mopsVersion,
                    dir: mopsSubdir ? [[mopsSubdir]] : [],
                    homepage: [[`https://github.com/${repo}`]]
                };
            }

            await onAddPackage(packageInfo);
            setInstalledPackages(prev => new Set([...prev, mopsPackageName.trim()]));

            // Reset form
            setMopsPackageName('');
            setMopsRepoUrl('');
            setMopsVersion('');
            setMopsSubdir('');
            setError(null);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to add package');
        }
    };

    const styles = {
        overlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
        },
        content: {
            backgroundColor: '#fff',
            borderRadius: '8px',
            padding: '24px',
            width: '600px',
            maxHeight: '80vh',
            overflowY: 'auto',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        },
        title: {
            fontSize: '24px',
            marginBottom: '24px',
            color: '#4B5320',
            fontWeight: '600',
        },
        tabs: {
            display: 'flex',
            marginBottom: '24px',
            borderBottom: '1px solid #ddd',
        },
        tab: {
            padding: '12px 24px',
            cursor: 'pointer',
            borderBottom: '2px solid transparent',
            color: '#666',
            fontSize: '16px',
        },
        activeTab: {
            borderBottom: '2px solid #8B4513',
            color: '#8B4513',
            fontWeight: '500',
        },
        packageList: {
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
        },
        packageItem: {
            padding: '16px',
            borderRadius: '6px',
            backgroundColor: '#f8f8f8',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        packageInfo: {
            flex: 1,
        },
        packageName: {
            fontWeight: '500',
            color: '#4B5320',
            fontSize: '16px',
        },
        packageDescription: {
            fontSize: '14px',
            color: '#666',
            marginTop: '4px',
        },
        button: {
            padding: '8px 16px',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'background-color 0.2s',
        },
        addButton: {
            backgroundColor: '#8B4513',
            color: 'white',
        },
        removeButton: {
            backgroundColor: '#dc3545',
            color: 'white',
        },
        footer: {
            marginTop: '24px',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
        },
        errorText: {
            color: '#dc3545',
            fontSize: '14px',
            marginTop: '8px',
        },
        formatSelector: {
            display: 'flex',
            marginBottom: '16px',
            borderBottom: '1px solid #eee',
            paddingBottom: '10px',
        },
        formatOption: {
            marginRight: '24px',
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
        },
        formatDescription: {
            fontSize: '13px',
            color: '#666',
            marginBottom: '16px',
            fontStyle: 'italic',
        },
        inputHelp: {
            fontSize: '12px',
            color: '#666',
            marginTop: '4px',
        },
    } as const;

    if (!isOpen) return null;

    return (
        <div style={styles.overlay as any}>
            <div style={styles.content as any}>
                <h2 style={styles.title}>Motoko Packages</h2>

                <div style={styles.tabs}>
                    <div
                        style={{
                            ...styles.tab,
                            ...(activeTab === 'vessel' ? styles.activeTab : {})
                        }}
                        onClick={() => setActiveTab('vessel')}
                    >
                        Vessel Package
                    </div>
                    <div
                        style={{
                            ...styles.tab,
                            ...(activeTab === 'mops' ? styles.activeTab : {})
                        }}
                        onClick={() => setActiveTab('mops')}
                    >
                        Custom Package
                    </div>
                </div>

                {activeTab === 'vessel' && (
                    <div style={styles.packageList}>
                        {VESSEL_PACKAGES.map((pkg) => {
                            const isInstalled = isPackageInstalled(pkg.name);

                            return (
                                <div key={pkg.name} style={styles.packageItem}>
                                    <div style={styles.packageInfo}>
                                        <div style={styles.packageName}>{pkg.name}</div>
                                        <div style={styles.packageDescription}>{pkg.description}</div>
                                    </div>
                                    <button
                                        onClick={() => handlePackageAction(pkg)}
                                        style={{
                                            ...styles.button,
                                            ...(isInstalled ? styles.removeButton : styles.addButton)
                                        }}
                                    >
                                        {isInstalled ? 'Remove' : 'Add'}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}

                {activeTab === 'mops' && (
                    <div style={{ padding: '20px' }}>
                        <div style={styles.formatSelector}>
                            <div
                                style={styles.formatOption}
                                onClick={() => setMopsFormat('github')}
                            >
                                <input
                                    type="radio"
                                    id="github-format"
                                    name="format"
                                    checked={mopsFormat === 'github'}
                                    onChange={() => setMopsFormat('github')}
                                />
                                <label htmlFor="github-format" style={{ marginLeft: '8px' }}>GitHub Package</label>
                            </div>
                            <div
                                style={styles.formatOption}
                                onClick={() => setMopsFormat('simple')}
                            >
                                <input
                                    type="radio"
                                    id="simple-format"
                                    name="format"
                                    checked={mopsFormat === 'simple'}
                                    onChange={() => setMopsFormat('simple')}
                                />
                                <label htmlFor="simple-format" style={{ marginLeft: '8px' }}>Version Only</label>
                            </div>
                        </div>

                        {mopsFormat === 'github' && (
                            <div style={styles.formatDescription}>
                                mops.toml format: <code>{mopsPackageName || 'package_name'} = "https://github.com/{mopsRepoUrl || 'owner/repo'}@{mopsVersion || 'branch'}"</code>
                                {mopsSubdir && <><br /><code>// With subdirectory: {mopsSubdir}</code></>}
                            </div>
                        )}

                        {mopsFormat === 'simple' && (
                            <div style={styles.formatDescription}>
                                mops.toml format: <code>{mopsPackageName || 'package_name'} = "{mopsVersion || 'version'}"</code>
                            </div>
                        )}

                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', marginBottom: '8px' }}>Package Name</label>
                            <input
                                type="text"
                                placeholder="e.g. base, pretty, etc."
                                value={mopsPackageName}
                                onChange={(e) => setMopsPackageName(e.target.value)}
                                style={{ width: '100%', padding: '8px' }}
                            />
                        </div>

                        {mopsFormat === 'github' && (
                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', marginBottom: '8px' }}>GitHub Repository</label>
                                <input
                                    type="text"
                                    placeholder="e.g. dfinity/motoko-base"
                                    value={mopsRepoUrl}
                                    onChange={(e) => setMopsRepoUrl(e.target.value)}
                                    style={{ width: '100%', padding: '8px' }}
                                />
                                <div style={styles.inputHelp}>
                                    Format: owner/repo or full URL: https://github.com/owner/repo
                                </div>
                            </div>
                        )}

                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', marginBottom: '8px' }}>
                                {mopsFormat === 'github' ? 'Branch/Version' : 'Version'}
                            </label>
                            <input
                                type="text"
                                placeholder={mopsFormat === 'github' ? "e.g. master, v1.0.0" : "e.g. 1.0.0"}
                                value={mopsVersion}
                                onChange={(e) => setMopsVersion(e.target.value)}
                                style={{ width: '100%', padding: '8px' }}
                            />
                        </div>

                        {mopsFormat === 'github' && (
                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', marginBottom: '8px' }}>Subdirectory (optional)</label>
                                <input
                                    type="text"
                                    placeholder="e.g. src"
                                    value={mopsSubdir}
                                    onChange={(e) => setMopsSubdir(e.target.value)}
                                    style={{ width: '100%', padding: '8px' }}
                                />
                            </div>
                        )}

                        <button
                            onClick={handleAddMopsPackage}
                            style={{...styles.button, ...styles.addButton}}
                        >
                            Add Package
                        </button>
                        {error && <div style={styles.errorText}>{error}</div>}
                    </div>
                )}

                <div style={styles.footer}>
                    <button
                        style={{...styles.button, backgroundColor: '#f3f4f6', color: '#374151'}}
                        onClick={onClose}
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MotokoPackagesDialog;